// Intermediate "Confirm Appointment" screen — between the user clicking
// Book Fitting and the actual POST /bookings write to SF.
//
// Shows a 10-minute hold notice + everything the user has chosen, with
// inline Change links to jump back to a particular field. On Confirm we
// fire the real SF write (delegated via onConfirm callback).
//
// Design comp reference: Screenshot 2026-05-25 6:18:49.
//
// In the international markets this screen also collects the phone number,
// required, since 2026-10-10 — the last thing asked before the booking is
// written. See CountryConfig.phoneOnConfirmStep for why it moved off Step 1.

import { useState } from 'react';
import styles from '../styles/widget.module.css';
import { PhoneField } from '../components/PhoneField';
import { SmsOptIn } from '../components/SmsOptIn';
import { collectsPhoneOnConfirm } from '../config/countries';
import { phoneComplete } from '../utils/phone';
import { AppointmentHoldNotice } from '../components/AppointmentHoldNotice';
import { formatSlotTime, formatLongDate } from '../utils/datetime';
import { isReadOnly } from '../utils/readOnly';
import type { BookingAction, BookingState } from '../state/machine';
import type { BookingEditField } from '../utils/bookingFlow';
import { useOpenClose } from '../hooks/useOpenClose';
import { TermsPopup } from '../components/TermsPopup';
import { defaultPromoTermsForCountry } from '../data/promoTerms';


import { formatPricingLines } from '../utils/pricing';
import { pricingLabelFor } from '../data/fittingTypes';
import { formatStorePhone } from '../utils/phone';
import { prebookingPhone } from '../utils/storePhone';

import { storeDisplayName } from '../data/storeDisplayNames';
import { promoPricingLines } from '../data/promoPricing';
import { promoPriceShowsFlatFee } from '../config/countries';
import { countryConfigOrDefault } from '../config/countries';
import { AlertMessage } from '../components/AlertMessage';
import swingerLogo from '../assets/images/swinger-logo.png';
import { resolveWireName } from '../data/fittingNames';

interface ConfirmAppointmentProps {
  state: BookingState;
  onConfirm: () => void;
  onEditField: (field: BookingEditField) => void;
  onHoldExpired?: () => void;
  /**
   * The phone was already collected, required, on Step 1 (the mandatory
   * flavour). Markets that collect it here skip the field when this is set,
   * so a mount pinned to `data-cc-phone="mandatory"` is never asked twice.
   */
  phoneMandatory?: boolean;
  dispatch: React.Dispatch<BookingAction>;
}

export function ConfirmAppointment({
  state,
  dispatch,
  onConfirm,
  onEditField,
  onHoldExpired,
  phoneMandatory = false,
}: ConfirmAppointmentProps) {
  const { isOpen: termsOpen, open: openTerms, close: closeTerms } = useOpenClose();
  // Reschedule variant derives from the state flag, NEVER from navigation —
  // the "Reschedule Your Appointment" header must survive every Change-button
  // round trip through the booking strip. Nothing is held for a looked-up
  // appointment, so the 10-minute hold notice (and its expiry callback) is
  // suppressed entirely.
  const reschedule = state.rescheduleMode;

  // International markets collect the phone HERE. It gates Confirm the way
  // Step 1's fields gate Continue: the button stays enabled, a click on an
  // incomplete number lights the field and says why, and nothing is written.
  const collectPhone = collectsPhoneOnConfirm(state.country, phoneMandatory, reschedule);
  const { smsOptInOffered, consent } = countryConfigOrDefault(state.country);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const phoneValid = phoneComplete(state.phone, state.country, state.dialCodeOverride);
  const phoneError = collectPhone && phoneTouched && !phoneValid;
  const handleConfirm = () => {
    if (isReadOnly()) return;
    if (collectPhone && !phoneValid) {
      setPhoneTouched(true);
      return;
    }
    onConfirm();
  };

  return (
    <div className={styles.confirmRoot}>
      <div className={styles.stepHeader}>
        <img
          src={swingerLogo}
          alt="Club Champion"
          className={styles.stripLogo}
          aria-hidden="true"
        />
        <div className={styles.stepTitle}>
          <h2 className={styles.confirmTitle}>
            {reschedule ? 'Reschedule Your Appointment' : 'Confirm Your Appointment'}
          </h2>
          {!reschedule && <AppointmentHoldNotice onExpired={onHoldExpired} />}
        </div>
      </div>
      <div className={styles.confirmGridWrapper}>
        <div className={styles.confirmGrid}>
          <div className={`${styles.confirmSection} ${styles.leftCol}`}>
            <h4>Your Information</h4>
            {/* A reschedule moves an EXISTING appointment: its identity is the
                two-factor key that authorised the lookup, so editing it here
                would detach the appointment from its owner. Identity is
                read-only in reschedule mode; the appointment keeps its person,
                and a wrong name/email routes through customer experience or a
                cancel-and-rebook. */}
            {state.rescheduleMode && (
              <p className={`${styles.rescheduleNote} ${styles.rescheduleNoteGray}`}>
                Want to change your name or email? Contact customer experience
                or cancel this fitting, and schedule a new one.
              </p>
            )}
            <div className={styles.infoRow}>
              <p className={styles.rowLabel}>
                <strong>Name</strong>{' '}
                {!state.rescheduleMode && (
                  <button
                    className={styles.changeLink}
                    data-cc-track="change_link"
                    data-cc-track-id="change_lead_name"
                    onClick={() => onEditField('lead')}
                  >
                    Change
                  </button>
                )}
              </p>
              <p className={styles.infoRowElFullWidth}>{state.fullName}</p>
            </div>
            <div className={styles.infoRow}>
              <p className={styles.rowLabel}>
                <strong>Email</strong>{' '}
                {!state.rescheduleMode && (
                  <button
                    className={styles.changeLink}
                    data-cc-track="change_link"
                    data-cc-track-id="change_lead_email"
                    onClick={() => onEditField('lead')}
                  >
                    Change
                  </button>
                )}
              </p>
              <p className={styles.infoRowElFullWidth}>{state.email}</p>
            </div>
            {/* Phone, one of three ways. International markets collect it on
                this screen, required, so the field itself renders here. In the
                mandatory flavour it was collected on Step 1 (shown here,
                editable). In the US optional flavour it isn't gathered until
                the post-booking step, so the row is hidden to avoid an empty
                field. */}
            {collectPhone && (
              <>
                <div className={styles.confirmPhoneField}>
                  <PhoneField
                    state={state}
                    dispatch={dispatch}
                    id="cc-phone-confirm"
                    invalid={phoneError}
                    onTouched={() => setPhoneTouched(true)}
                  />
                  {phoneError && (
                    <p className={styles.confirmPhoneError} id="cc-phone-confirm-error" role="alert">
                      Please enter a valid phone number to confirm your appointment.
                    </p>
                  )}
                </div>
                {/* AU folds SMS into its single Step 1 marketing box, so the
                    separate checkbox is suppressed where consent.combined is
                    set; the UK sends no SMS at all. */}
                {smsOptInOffered && !consent?.combined && (
                  <div className={styles.confirmSmsOptIn}>
                    <SmsOptIn state={state} dispatch={dispatch} />
                  </div>
                )}
              </>
            )}
            {!collectPhone && state.phone.trim() !== '' && (
              <div className={styles.infoRow}>
                <p className={styles.rowLabel}>
                  <strong>Phone</strong>{' '}
                  {!state.rescheduleMode && (
                    <button
                      className={styles.changeLink}
                      data-cc-track="change_link"
                      data-cc-track-id="change_lead_phone"
                      onClick={() => onEditField('lead')}
                    >
                      Change
                    </button>
                  )}
                </p>
                <p className={styles.infoRowElFullWidth}>{state.phone}</p>
              </div>
            )}

          {/* "How did you hear about us?" used to sit here. It has moved to the
              confirmation screen, where CC drew it originally.

              It was placed on this step only because Salesforce had nowhere to
              put it afterwards: PostAppointmentUpdate took phone and SMS
              consent and nothing else, so asked after booking the answer had
              no home. CC added hearAbout to that endpoint on 2026-08-27, so the
              constraint is gone and the question now lives on the same screen
              as the submit that sends it. See steps/GetSetUp.tsx. */}
          </div>

          <div className={`${styles.confirmDetails} ${styles.rightCol}`}>
            <div className={styles.confirmSection}>
              <h4>
                Your Fitting Details
              </h4>
              {/* The green counterpart of the identity note: this column is where
                  reschedule edits DO live, so the callout draws the eye here. */}
              {state.rescheduleMode && (
                <p className={`${styles.rescheduleNote} ${styles.rescheduleNoteGreen}`}>
                  Click Change to modify aspects of your fitting or to choose a new
                  date and time.
                </p>
              )}
              <div className={styles.infoRow}>
                <p className={styles.rowLabel}>
                  <strong>Location</strong>{' '}
                  <button
                    className={styles.changeLink}
                    data-cc-track="change_link"
                    data-cc-track-id="change_location"
                    onClick={() => onEditField('location')}
                  >
                    Change
                  </button>
                </p>
                <p>
                  <strong>
                    {storeDisplayName(state.selectedLocation?.name ?? '', state.country)}
                  </strong>
                </p>
                {state.selectedLocation?.address && <p>{state.selectedLocation.address}</p>}
                {/* The studio's own contact details, so a customer with a
                    question before confirming can reach the people who will
                    actually run the fitting. Omitted per-field when SF has
                    nothing on file rather than rendering an empty line.
                    Labelled and formatted: this screen printed the number raw
                    and unlabelled, so an international store read as
                    "441293978944" with nothing saying what it was. */}
                {/* Before booking, the number is the studio's Customer
                    Experience line (publicPhone); the booking desk's number
                    is reserved for the post-booking confirmation (CC,
                    2026-10-26). */}
                {prebookingPhone(state.selectedLocation) && (
                  <>
                    <p className={styles.rowLabel}><strong>Studio Phone</strong></p>
                    <p>
                      <a
                        href={`tel:${prebookingPhone(state.selectedLocation)!.replace(/[^\d+]/g, '')}`}
                        className={styles.classicGreenLink}
                      >
                        {formatStorePhone(prebookingPhone(state.selectedLocation)!, state.country)}
                      </a>
                    </p>
                  </>
                )}
                {state.selectedLocation?.email && (
                  <p>
                    <a
                      href={`mailto:${state.selectedLocation.email}`}
                      className={styles.classicGreenLink}
                    >
                      {state.selectedLocation.email}
                    </a>
                  </p>
                )}
              </div>
              <div className={styles.infoRow}>
                <p className={styles.rowLabel}>
                  <strong>Date &amp; Time</strong>{' '}
                  <button
                    className={styles.changeLink}
                    data-cc-track="change_link"
                    data-cc-track-id="change_date"
                    onClick={() => onEditField('dateTime')}
                  >
                    Change
                  </button>
                </p>
                <p>{state.selectedDate ? formatLongDate(state.selectedDate, countryConfigOrDefault(state.country).locale) : ''}</p>
                <p>{state.selectedTime ? formatSlotTime(state.selectedTime) : ''}</p>
              </div>
            </div>

            <div className={styles.confirmSection}>
              <div className={styles.infoRow}>
                <p className={styles.rowLabel}>
                  <strong>Service</strong>{' '}
                  <button
                    className={styles.changeLink}
                    data-cc-track="change_link"
                    data-cc-track-id="change_fitting_type"
                    onClick={() => onEditField('fittingType')}
                  >
                    Change
                  </button>
                </p>
                <p>{state.selectedFittingType?.displayName ?? state.selectedFittingType?.name}</p>
                <p>{state.selectedFittingType?.durationLabel}</p>
              </div>
              <div className={styles.infoRow}>
                <p className={styles.rowLabel}>
                  <strong>Fitter</strong>
                </p>
                <p>{state.selectedSlot?.fitterName ?? 'Any Fitter'}</p>
              </div>
              <div className={styles.infoRow}>
                <p className={styles.rowLabel}>
                  <strong>Fitting Fee</strong>
                </p>
                {state.selectedFittingType && (() => {
                  // Use pricingLabelFor so the confirmation reflects the same
                  // country-override + promo-window swap that step 2's fitting
                  // picker applies. Prior code always rendered the base
                  // pricingLabel here — mismatched with what the user just saw
                  // when a promo was active.
                  const confirmWireName =
                    resolveWireName(state.selectedFittingType.name, state.fittingNameMap);
                  const { line1, line2 } =
                    promoPricingLines(
                      state.promoPricing?.[confirmWireName],
                      promoPriceShowsFlatFee(state.country),
                      state.country,
                    ) ??
                    formatPricingLines(
                      pricingLabelFor(state.selectedFittingType, state.country),
                      promoPriceShowsFlatFee(state.country),
                    );
                  return (
                    <>
                      <p style={{ margin: 0 }}>
                        {line1.bold && <strong>{line1.bold}</strong>}
                        {line1.rest}
                      </p>
                      <p style={{ margin: 0 }}>
                        {line2.bold && <strong>{line2.bold}</strong>}
                        {line2.rest}
                      </p>
                    </>
                  );
                })()}
                {(() => {
                  // The visitor's own code, echoed back so the confirmation
                  // accounts for the price above it. The master promotion is
                  // deliberately not shown: it is applied silently, the input
                  // never carries it, and naming a code the visitor never
                  // entered reads as something they have to act on.
                  //
                  // Change returns to the fitting grid rather than opening an
                  // editor here — the promo field lives on that step, beside
                  // the prices the code changes.
                  const applied =
                    state.promoValidation?.valid && state.promoValidatedCode?.trim()
                      ? state.promoValidatedCode.trim()
                      : null;
                  if (!applied) return null;
                  return (
                    <p className={styles.confirmPromoApplied}>
                      <strong>{applied}</strong> Promo applied.{' '}
                      <button
                        className={styles.changeLink}
                        data-cc-track="change_link"
                        data-cc-track-id="change_promo_code"
                        onClick={() => onEditField('fittingType')}
                      >
                        Change
                      </button>
                    </p>
                  );
                })()}
              </div>
              <div className={styles.pricingDisclaimer} style={{ position: 'relative' }}>
                <span>You are under no obligation to buy custom clubs.</span>
                <button
                  type="button"
                  onClick={openTerms}
                  className={styles.classicGreenLink}
                  style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer' }}
                >
                  See Terms and Conditions
                </button>
                <TermsPopup
                  isOpen={termsOpen}
                  onClose={closeTerms}
                  title="Terms & Conditions"
                  body={(() => {
                    // Render the applied master promo's terms — set on
                    // store-select from /fittings.activePromoTerms, or the
                    // SF-fetched FITTINGUS copy as legacy fallback, or the
                    // bundled stub on first paint. The prior implementation
                    // always rendered FITTINGUS at the top with the master's
                    // terms as a secondary paragraph, which was wrong for
                    // international bookings (FITTINGUS doesn't apply to UK/
                    // CA/AU — the country-specific master is the ONLY promo).
                    const terms =
                      state.promoTerms ||
                      state.defaultPromoTermsLive ||
                      defaultPromoTermsForCountry(state.country);
                    return <p style={{ margin: 0 }}>{terms}</p>;
                  })()}
                  className={styles.confirmTermsPopup}
                />
              </div>
            </div>
          </div>
        </div>

        {state.submitError && (
          <AlertMessage
            title={reschedule ? 'Reschedule failed' : 'Booking failed'}
            description={state.submitError}
          />
        )}

        <div className={styles.confirmFooter}>
          <button
            className={`${styles.button} ${isReadOnly() ? styles.buttonAriaDisabled : ''}`}
            disabled={state.submitting}
            aria-disabled={isReadOnly() || undefined}
            data-cc-track="cta"
            data-cc-track-id="confirm_appointment"
            onClick={handleConfirm}
          >
            {state.submitting ? (
              <>
                <span className={styles.spinner} />
                {reschedule ? 'Rescheduling…' : 'Booking…'}
              </>
            ) : isReadOnly() ? (
              reschedule ? 'Reschedule (disabled)' : 'Confirm Appointment (disabled)'
            ) : reschedule ? (
              'Reschedule'
            ) : (
              'Confirm Appointment'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
